// Vinodha Diagnostics — site-wide content & contact constants
// ──────────────────────────────────────────────────────────────
// 👉 EVERYTHING editable lives here. To update the logo, photos,
//    doctor details, timings, services, packages or contact info,
//    change the values below — no layout/design changes required.
// ──────────────────────────────────────────────────────────────

// All imagery is centralised here so it can be swapped easily.
// Replace any URL below with your own uploaded image link later.
export const IMAGES = {
  // Brand logo (uploaded — VD LOGO). Easy to replace at /public/brand/.
  logo: "/brand/vd-logo-trim.png",
  logoMark: "/brand/vd-mark.png",
  // Hero — Indian female radiologist performing an ultrasound (uploaded).
  hero: "/images/ultrasound-1.jpg",
  about: "/images/indian-consult-2.jpg",
  // Doctor profile photos (uploaded Indian healthcare professionals).
  // doctorMale uses a professional Indian male-doctor placeholder until a real
  // photograph of Dr. Udayakumar is provided — swap the file below to replace.
  doctorFemale: "/images/indian-consult-1.jpg",
  doctorMale: "/images/doctor-male-placeholder.jpg",
  // Service thumbnails — Indian healthcare imagery for the patient-facing
  // scans; neutral equipment photography for the rest (no stock male doctors).
  service: {
    general: "/images/ultrasound-1.jpg",
    pregnancy: "/images/indian-consult-2.jpg",
    ultrasound: "/images/indian-consult-1.jpg",
    doppler:
      "https://images.unsplash.com/photo-1581056771107-24ca5f033842?auto=format&fit=crop&w=800&q=70",
    thyroid:
      "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=800&q=70",
    breast:
      "https://images.unsplash.com/photo-1581595220892-b0739db3ba8c?auto=format&fit=crop&w=800&q=70",
    pelvic:
      "https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=800&q=70",
  },
};

export const SITE = {
  name: "Vinodha Diagnostics",
  tagline: "Accurate Diagnostics. Trusted Care.",
  subTagline: "Advanced Diagnostic Services with Expert Radiologist Reporting",
  // Top announcement bar — edit or set show:false to hide.
  announcement: {
    show: true,
    title: "Special Sunday Morning Consultation Available",
    detail: "Appointments open on Sundays (Morning Session). For advance booking, please contact us.",
  },
  // Primary contact (Hosa Road — Main Office)
  phone: "+91 91080 80666",
  phoneRaw: "+919108080666",
  phoneAlt: "080 4814 7411",
  phoneAltRaw: "08048147411",
  whatsapp: "+91 91080 80666",
  whatsappRaw: "919108080666",
  email: "info@vinodhadiagnostics.in", // placeholder — update once finalised
  // Google Maps (Hosa Road main office) — exact location provided by client.
  mapsLink: "https://maps.app.goo.gl/WtgBwvypznCqMtSm8",
  mapsEmbed:
    "https://www.google.com/maps?q=Vinodha+Diagnostics,+Hosa+Road,+Bengaluru&ll=12.87288,77.6515445&z=17&output=embed",
  branches: [
    {
      name: "Hosa Road",
      label: "Main Office",
      isMain: true,
      address:
        "Hosur Road, State Bank of Patiala Building, Hosa Road, Bengaluru – 560100",
      phones: ["+91 91080 80666", "080 4814 7411"],
      phoneRaw: "+919108080666",
      doctor: {
        name: "Dr. Hithishini H",
        qualifications: "MDRD, DNB",
        title: "Consultant Radiologist",
        isFemale: true,
        photo: IMAGES.doctorFemale,
      },
      morning: "10:30 AM – 3:00 PM",
      evening: "6:00 PM – 9:00 PM",
      mapLink: "https://maps.app.goo.gl/WtgBwvypznCqMtSm8",
      mapEmbed:
        "https://www.google.com/maps?q=Vinodha+Diagnostics,+Hosa+Road,+Bengaluru&ll=12.87288,77.6515445&z=17&output=embed",
    },
    {
      name: "Hebbagodi",
      label: "Branch",
      isMain: false,
      address:
        "Next to SFS School, Hosur Main Road, Hebbagodi, Bengaluru – 560100",
      phones: ["+91 91080 80666"],
      phoneRaw: "+919108080666",
      doctor: {
        name: "Dr. Udayakumar",
        qualifications: "MDRD",
        title: "Consultant Radiologist",
        isFemale: false,
        photo: IMAGES.doctorMale,
      },
      morning: "11:00 AM – 3:00 PM",
      evening: "7:00 PM – 9:00 PM",
      mapLink: "https://maps.app.goo.gl/FmqssEWFv1jVvzri7",
      mapEmbed:
        "https://www.google.com/maps?q=Vinodha+Diagnostics,+Hebbagodi,+Bengaluru&ll=12.8284248,77.682102&z=17&output=embed",
    },
  ],
  social: {
    facebook: "https://facebook.com",
    instagram: "https://instagram.com",
    youtube: "https://youtube.com",
    linkedin: "https://linkedin.com",
  },
};

export const SERVICES = [
  {
    slug: "general-ultrasound",
    name: "General Ultrasound",
    desc: "Abdomen, KUB & soft-tissue imaging with senior radiologist reporting.",
    icon: "Activity",
    image: IMAGES.service.general,
  },
  {
    slug: "pregnancy-scans",
    name: "Pregnancy Scans",
    desc: "Dating, anomaly, growth & well-being scans by senior radiologists.",
    icon: "HeartPulse",
    image: IMAGES.service.pregnancy,
  },
  {
    slug: "doppler-studies",
    name: "Doppler Studies",
    desc: "Arterial, venous & obstetric colour Doppler evaluations.",
    icon: "Waves",
    image: IMAGES.service.doppler,
  },
  {
    slug: "thyroid-scans",
    name: "Thyroid Scans",
    desc: "High-frequency thyroid & neck ultrasound with detailed reporting.",
    icon: "Stethoscope",
    image: IMAGES.service.thyroid,
  },
  {
    slug: "breast-scans",
    name: "Breast Scans",
    desc: "Breast ultrasound for lumps, screening & follow-up, reported with care.",
    icon: "ShieldCheck",
    image: IMAGES.service.breast,
  },
  {
    slug: "pelvic-scans",
    name: "Pelvic Scans",
    desc: "Transabdominal & transvaginal pelvic ultrasound for gynae assessment.",
    icon: "ScanLine",
    image: IMAGES.service.pelvic,
  },
  {
    slug: "ultrasound-services",
    name: "Ultrasound Services",
    desc: "Comprehensive diagnostic ultrasound across all body systems.",
    icon: "Droplet",
    image: IMAGES.service.ultrasound,
  },
];

// Health Check-up Packages — pricing intentionally hidden for now.
// To show prices later, add a `price` field and update Packages.jsx.
export const PRICING_NOTE =
  "Please contact us for current pricing and appointment bookings.";

export const PACKAGES = [
  {
    name: "Basic Health Check",
    tag: "Essentials",
    features: [
      "Complete Blood Count (CBC)",
      "Fasting Blood Sugar",
      "Urine Routine",
      "Lipid Profile",
      "Doctor Consultation",
    ],
    popular: false,
  },
  {
    name: "Comprehensive Care",
    tag: "Most Popular",
    features: [
      "Everything in Basic",
      "Liver Function Test",
      "Kidney Function Test",
      "Thyroid Profile (T3, T4, TSH)",
      "Vitamin D3 & B12",
      "ECG",
    ],
    popular: true,
  },
  {
    name: "Premium Full Body",
    tag: "Executive",
    features: [
      "Everything in Comprehensive",
      "Diabetes Panel (HbA1c)",
      "Cardiac Risk Markers",
      "Ultrasound Abdomen",
      "Senior Physician Review",
      "Detailed Health Report",
    ],
    popular: false,
  },
];

export const TESTIMONIALS = [
  {
    name: "Anitha R.",
    role: "Electronic City",
    quote:
      "Got my ultrasound done at the Hosa Road branch — the radiologist explained everything patiently. Reports were ready the same evening.",
    rating: 5,
  },
  {
    name: "Vikram S.",
    role: "Hebbagodi",
    quote:
      "Booked a full check-up for my parents. Friendly staff, clean premises and a very caring team. Highly recommended.",
    rating: 5,
  },
  {
    name: "Priya M.",
    role: "Bommasandra",
    quote:
      "Pregnancy scans here are reassuring. A senior female radiologist, great equipment and zero rush — felt very cared for.",
    rating: 5,
  },
  {
    name: "Rahul K.",
    role: "Singasandra",
    quote:
      "Thyroid scan and blood work on the same visit. Quick turnaround and digital reports on WhatsApp — exactly what I needed.",
    rating: 5,
  },
  {
    name: "Lakshmi N.",
    role: "Begur Road",
    quote:
      "Reports came quickly with a clear explanation. Polite team and transparent billing. Will definitely recommend to family.",
    rating: 5,
  },
];

export const USPS = [
  { title: "Accurate Reports", desc: "Careful processes & double-checked radiologist reporting.", icon: "ShieldCheck" },
  { title: "Female Radiologist", desc: "Dr. Hithishini H available at the Hosa Road branch.", icon: "UserCheck" },
  { title: "Modern Equipment", desc: "Latest ultrasound & Doppler imaging systems.", icon: "Cpu" },
  { title: "Quick Turnaround", desc: "Same-day digital reports on WhatsApp.", icon: "Timer" },
  { title: "Two Convenient Branches", desc: "Hosa Road & Hebbagodi, South Bangalore.", icon: "MapPin" },
];
